// src/pages/league-dashboard/team-home/WatchListWidget.js
//
// Extracted from inline definition in TeamHomeDashboard.js per Tier 2
// #9 commit 9b (refactor only — no behavior change).
//
// WATCH LIST WIDGET - Simplified 3-line watch list for dashboard
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { DynastyTable } from '../../../services/tableService';
import apiService from '../../../services/apiService';
import { createWatchListWidgetColumns } from '../../../services/tables/watchListWidgetColumns';
import { isPitcherPosition } from '../../../utils/playerClassification';

const WatchListWidget = ({ league }) => {
  const [watchListData, setWatchListData] = useState({ players: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hitting');

  useEffect(() => {
    loadWatchList();
  }, []);

  const loadWatchList = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/watchlist');
      if (response.data?.success) {
        setWatchListData({
          players: response.data.players || [],
          count: response.data.count || 0
        });
      }
    } catch (err) {
      console.error('Error loading watch list:', err);
    } finally {
      setLoading(false);
    }
  };

  // Organize players by type (CANONICAL)
  const organizedPlayers = React.useMemo(() => {
    const organized = { hitting: [], pitching: [] };
    watchListData.players.forEach(player => {
      const isPitcher = isPitcherPosition(player.info?.position);
      if (isPitcher) {
        organized.pitching.push(player);
      } else {
        organized.hitting.push(player);
      }
    });
    return organized;
  }, [watchListData]);

  const currentPlayers = activeTab === 'hitting' ? organizedPlayers.hitting : organizedPlayers.pitching;
  const isPitcher = activeTab === 'pitching';
  const columns = createWatchListWidgetColumns(league, isPitcher);

  if (loading) {
    return (
      <div className={dynastyTheme.components.card.base}>
        <div className="p-3">
          <h3 className={dynastyTheme.components.heading.h3}>⭐ Watch List</h3>
          <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dynastyTheme.classes.border.primary} mx-auto mb-4`}></div>
            Loading watch list...
          </div>
        </div>
      </div>
    );
  }

  if (!watchListData.players.length) {
    return (
      <div className={dynastyTheme.components.card.base}>
        <div className="p-3">
          <h3 className={dynastyTheme.components.heading.h3}>⭐ Watch List</h3>
          <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
            <div className="text-5xl mb-3">⭐</div>
            <p className="text-lg">No players on watch list</p>
            <p className={`text-sm mt-2 ${dynastyTheme.classes.text.neutralLighter}`}>
              Add players to track their performance
            </p>
            <button
              onClick={() => window.location.href = '#/watch-list'}
              className={`${dynastyTheme.utils.getComponent('button', 'primary', 'md')} mt-4`}
            >
              Go to Watch List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={dynastyTheme.components.card.base}>
      <div className="p-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className={dynastyTheme.components.heading.h3}>⭐ Watch List</h3>
          <button
            onClick={() => window.location.href = '#/watch-list'}
            className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
          >
            View All
          </button>
        </div>

        {/* Tab Headers */}
        <div className={`flex border-b ${dynastyTheme.classes.border.neutral} mb-4`}>
          <button
            onClick={() => setActiveTab('hitting')}
            className={`px-4 py-2 font-medium ${dynastyTheme.classes.transition} ${
              activeTab === 'hitting'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                : dynastyTheme.classes.text.neutralLight
            }`}
          >
            Hitters ({organizedPlayers.hitting.length})
          </button>
          <button
            onClick={() => setActiveTab('pitching')}
            className={`px-4 py-2 font-medium ${dynastyTheme.classes.transition} ml-6 ${
              activeTab === 'pitching'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                : dynastyTheme.classes.text.neutralLight
            }`}
          >
            Pitchers ({organizedPlayers.pitching.length})
          </button>
        </div>

        {/* Legend */}
        <div className={`text-xs mb-4 p-3 ${dynastyTheme.classes.bg.darkLighter} rounded-lg flex gap-6`}>
          <div className={dynastyTheme.classes.text.neutralLight}>
            <span className="font-bold">White:</span> Full Season
          </div>
          <div className={dynastyTheme.classes.text.warning}>
            <span className="font-bold">Orange:</span> Last 14 Days
          </div>
        </div>

        {/* Table Content */}
        {currentPlayers.length > 0 ? (
          <DynastyTable
            data={currentPlayers}
            columns={columns}
            tableType="player-list"
            rowsPerItem={2}
            rowTypes={['season', 'rolling']}
            maxHeight="400px"
          />
        ) : (
          <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-8`}>
            No {activeTab === 'hitting' ? 'hitters' : 'pitchers'} on watch list
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchListWidget;
